// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — client-side loopback connect-target allowlist (ABB-S3)
// ═══════════════════════════════════════════════════════════════════════════
//
// mirrors apps/node-companion/src/security.ts (the companion's loopback NAME set).
// This is the CLIENT side of the same defence (spec §3 / SM-11 / INV-LOOPBACK):
// the companion's `assertLoopbackBind` / `isLoopbackHost` guard the BIND + the
// inbound `Host` header; THIS guard checks the connect TARGET so a future
// mis-configured `wsUrl` cannot point the Bearer-token-carrying socket at a
// remote host (P2 token-isolation). The connect URL is HARDCODED to loopback in
// `agentBridgeClient.ts`, but the allowlist is a structural backstop that fails
// closed for any non-loopback authority.
//
// LEAF module: imports NOTHING (no editorStore, no node-companion, no contracts).
// A pure URL→verdict function so it is unit-testable without a socket and adds
// no edge to the dependency graph (ПРИОРИТЕТ #2).
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Loopback host NAMES the client may connect to. Mirrors
 * `LOOPBACK_HOST_NAMES` in apps/node-companion/src/security.ts. `::1` is the
 * IPv6 loopback; in a URL authority it appears bracketed as `[::1]`, normalized
 * away before this check.
 */
const LOOPBACK_HOST_NAMES = new Set(["127.0.0.1", "localhost", "::1"]);

/** WebSocket schemes the bridge connect target may use (loopback `ws`/`wss`). */
const LOOPBACK_WS_SCHEMES = new Set(["ws:", "wss:"]);

/**
 * Strip IPv6 brackets from a `URL.hostname`. The WHATWG `URL` parser already
 * removes the brackets for a bracketed IPv6 literal (`ws://[::1]:8765` →
 * `hostname === "::1"`), but normalize defensively in case a raw authority is
 * passed.
 */
function normalizeHostname(hostname: string): string {
  const trimmed = hostname.trim();
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

/**
 * Whether a connect-target URL is a loopback WebSocket authority (§3 / SM-11).
 * Fails CLOSED: a non-`ws`/`wss` scheme, a non-loopback host, a parse failure,
 * or any malformed authority returns `false` (the client refuses to open the
 * socket and never sends the token off-loopback). Port is NOT constrained here
 * (the hardcoded URL pins 8765; the allowlist guards the HOST + SCHEME only).
 */
export function isLoopbackWsTarget(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false; // unparseable → fail closed.
  }
  if (!LOOPBACK_WS_SCHEMES.has(parsed.protocol)) {
    return false; // only ws:// / wss:// loopback targets.
  }
  const host = normalizeHostname(parsed.hostname);
  if (host.length === 0) {
    return false;
  }
  return LOOPBACK_HOST_NAMES.has(host.toLowerCase());
}

/**
 * Assert a connect-target URL is loopback or throw. Used by the client before
 * `new WebSocket(...)` so a non-loopback target is a hard, surfaced failure
 * rather than a silent off-loopback token leak.
 */
export function assertLoopbackWsTarget(url: string): void {
  if (!isLoopbackWsTarget(url)) {
    throw new Error(
      `agent-bridge: refusing to connect to non-loopback target "${url}" (loopback-only, INV-LOOPBACK)`,
    );
  }
}
